import useIsMobile from '@/hooks/use-is-mobile';
import { useEffect, useRef, useState } from 'react';

export type SectionRefs = {
  what: React.RefObject<HTMLDivElement>;
  why: React.RefObject<HTMLDivElement>;
  how: React.RefObject<HTMLDivElement>;
  insurance: React.RefObject<HTMLDivElement>;
  cost: React.RefObject<HTMLDivElement>;
  value: React.RefObject<HTMLDivElement>;
};

export default function useTestDetailsHeaderController(onRefsCreated: (refs: SectionRefs) => void, navItems: string[]) {
  const isMobile = useIsMobile();
  const [activeItem, setActiveItem] = useState<string>(navItems.length > 0 ? navItems[0] : 'insurance');
  const [refsCreated, setRefsCreated] = useState(false);
  const [observer, setObserver] = useState<IntersectionObserver | null>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sectionRefs: SectionRefs = {
    what: useRef<HTMLDivElement>(null),
    why: useRef<HTMLDivElement>(null),
    how: useRef<HTMLDivElement>(null),
    insurance: useRef<HTMLDivElement>(null),
    cost: useRef<HTMLDivElement>(null),
    value: useRef<HTMLDivElement>(null),
  };

  // Create the observer once
  useEffect(() => {
    const newObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          Object.entries(sectionRefs).forEach(([key, ref]) => {
            if (ref.current === entry.target && entry.isIntersecting) {
              setActiveItem(key);
            }
          });
        });
      },
      {
        root: null,
        rootMargin: '-50% 0px',
        threshold: 0,
      }
    );

    setObserver(newObserver);

    return () => {
      newObserver.disconnect();
    };
  }, []);

  // Pass refs up to parent
  useEffect(() => {
    if (!refsCreated) {
      onRefsCreated(sectionRefs);
      setRefsCreated(true);
    }
  }, [onRefsCreated, refsCreated]);

  // Observe refs after they're connected to DOM elements
  useEffect(() => {
    if (!observer || !refsCreated) return;

    // First disconnect any existing observations
    observer.disconnect();

    // Then observe all sections that have DOM elements
    Object.values(sectionRefs).forEach((ref) => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    return () => observer.disconnect();
  }, [observer, refsCreated, sectionRefs]);

  const handleClick = (itemId: keyof SectionRefs) => (e: React.MouseEvent) => {
    e.preventDefault();
    setActiveItem(itemId);
    sectionRefs[itemId].current?.scrollIntoView({ behavior: 'smooth' });
  };

  return {
    activeItem,
    handleClick,
    isMobile,
  };
}
